import React, { useState, useRef } from "react";
import { Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Form,
  SimpleItem,
  GroupItem,
  Label,
  RequiredRule,
} from "devextreme-react/form";
import { Button as DxButton } from "devextreme-react/button";
import notify from "devextreme/ui/notify";
import { useAuth } from "hooks/useAuth";

const BugReportForm = ({ onClose }) => {
  const { account } = useAuth();
  const formRef = useRef(null);
  const [formData, setFormData] = useState({
    name: account.name,
    email: account.username,
    category: "",
    urgency: "normal",
    steps: "",
    expected: "",
    actual: "",
    contactMe: true,
  });
  const [file, setFile] = useState(null);

  const categories = [
    "User Interface",
    "Features",
    "Performance",
    "System Error",
    "Data Problem",
    "Other",
  ];

  const urgencyLevels = [
    { text: "Low", value: "low", color: "success" },
    { text: "Normal", value: "normal", color: "info" },
    { text: "High", value: "high", color: "warning" },
    { text: "Critical", value: "critical", color: "danger" },
  ];

  const handleFieldChange = (e) => {
    if (e.name) {
      setFormData({
        ...formData,
        [e.name]: e.value,
      });
    }
  };

  const handleFileUploaded = (e) => {
    if (e.value && e.value.length > 0) {
      setFile(e.value[0]);
    } else {
      setFile(null);
    }
  };

  const validateFileSize = (e) => {
    if (e.value && e.value.length > 0) {
      const file = e.value[0];
      if (file.size > 20 * 1024 * 1024) {
        e.cancel = true;
        notify("The file is too large (max 20MB)", "error", 3000);
      }
    }
  };

  const handleSubmit = () => {
    const { isValid } = formRef.current.instance().validate();

    if (!isValid) {
      return;
    }

    console.log("Submitted data:", formData);
    console.log("Attached file:", file);

    notify({
      message: "Thank you for your report! We will examine this issue.",
      type: "success",
      displayTime: 4000,
      position: { at: "top center", my: "top center" },
    });

    setFormData({
      name: "",
      email: "",
      category: "",
      urgency: "normal",
      steps: "",
      expected: "",
      actual: "",
      contactMe: false,
    });
    setFile(null);

    onClose();
  };

  return (
    <div>
      <Button variant="link" className="mb-3 ps-0" onClick={onClose}>
        <FontAwesomeIcon icon="arrow-left" className="me-2" />
        Retour
      </Button>

      <h4 className="mb-3">Bug Reporting</h4>

      <Form
        ref={formRef}
        formData={formData}
        onFieldDataChanged={handleFieldChange}
        labelMode="floating"
      >
        <GroupItem colCount={2}>
          <SimpleItem
            dataField="name"
            editorType="dxTextBox"
            editorOptions={{}}
          >
            <RequiredRule message="The name is required" />
            <Label text="Name" />
          </SimpleItem>

          <SimpleItem
            dataField="email"
            editorType="dxTextBox"
            editorOptions={{
              mode: "email",
            }}
          >
            <RequiredRule message="The email is required" />
            <Label text="Email" />
          </SimpleItem>
        </GroupItem>

        <GroupItem colCount={2}>
          <SimpleItem
            dataField="category"
            editorType="dxSelectBox"
            editorOptions={{
              items: categories,
              placeholder: "",
            }}
          >
            <RequiredRule message="The category is required" />
            <Label text="Category" />
          </SimpleItem>

          <SimpleItem
            dataField="urgency"
            editorType="dxRadioGroup"
            editorOptions={{
              items: urgencyLevels,
              layout: "horizontal",
              valueExpr: "value",
              displayExpr: "text",
            }}
          >
            <Label text="Urgency Level" />
          </SimpleItem>
        </GroupItem>

        <GroupItem>
          <SimpleItem
            dataField="steps"
            editorType="dxTextArea"
            editorOptions={{
              height: 80,
              maxLength: 500,
              spellcheck: true,
            }}
          >
            <RequiredRule message="The steps to reproduce are required" />
            <Label text="Steps to reproduce" />
          </SimpleItem>
        </GroupItem>

        <GroupItem colCount={2}>
          <SimpleItem
            dataField="expected"
            editorType="dxTextArea"
            editorOptions={{
              height: 80,
              maxLength: 300,
              spellcheck: true,
            }}
          >
            <RequiredRule message="This field is required" />
            <Label text="Expected behavior" />
          </SimpleItem>

          <SimpleItem
            dataField="actual"
            editorType="dxTextArea"
            editorOptions={{
              height: 80,
              maxLength: 300,
              spellcheck: true,
            }}
          >
            <RequiredRule message="This field is required" />
            <Label text="Actual behavior" />
          </SimpleItem>
        </GroupItem>

        <GroupItem>
          <SimpleItem
            editorType="dxFileUploader"
            editorOptions={{
              selectButtonText: "Select file",
              multiple: false,
              accept: "image/*,.pdf",
              uploadMode: "useForm",
              labelText: "or drop a file here",
              onValueChanged: handleFileUploaded,
              onUploadStarted: validateFileSize,
            }}
          >
            <Label text="Screenshot or document (max 20MB)" />
          </SimpleItem>
          {file && (
            <div className="mt-2 d-flex align-items-center">
              <FontAwesomeIcon
                icon={file.type.includes("image") ? "image" : "file-pdf"}
                className="me-2 text-primary"
              />
              <span className="text-truncate">{file.name}</span>
              <span className="ms-2 text-muted">
                ({Math.round(file.size / 1024)} KB)
              </span>
            </div>
          )}
        </GroupItem>

        <GroupItem>
          <SimpleItem
            dataField="contactMe"
            editorType="dxCheckBox"
            editorOptions={{
              text: "I would like to be contacted about this report",
            }}
          />
        </GroupItem>
      </Form>

      <div className="d-flex justify-content-end mt-4">
        <DxButton
          text="Cancel"
          type="danger"
          stylingMode="outlined"
          onClick={onClose}
          className="me-2"
        />
        <DxButton
          text="Send"
          type="success"
          stylingMode="outlined"
          icon="sendfilled"
          onClick={handleSubmit}
        />
      </div>
    </div>
  );
};

export default BugReportForm;
